import { Component, inject, OnDestroy, OnInit } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { DomSanitizer } from '@angular/platform-browser'
import { ActivatedRoute, Router } from '@angular/router'
import { map, Observable } from 'rxjs'

import {
  CollectionRecord,
  Company,
  ContentRecord,
  Deploy,
  Enums,
  Environment,
  GeneratedContentDetails,
  Hunt,
  HuntPackageRecord,
  HuntPackagesAdded,
  HuntSummary,
  HuntTemplate,
  QueryLogic,
  ThreatProfile,
} from '@cyborg-monorepo/hunter/common'
import { SupportedTools, toolFactory } from '@cyborg-monorepo/hunter/matchboi'
import {
  DialogRef,
  DialogService,
  SnackbarService,
  ThemeService,
} from '@hyperkit'

import { AuthService } from '../../../../auth/auth.service'
import { FeatureFlagService } from '../../../../helpers/feature-flag.service'
import {
  gatherTags,
  isHuntPackageHuntCompatible,
  supportedToolUrl,
} from '../../../../helpers/utils'
import {
  TrackingEventEnum,
  TrackingService,
} from '../../../../tracking/tracking.service'
import { AddToQuickHuntDialogComponent } from '../../../shared/components/dialogs/add-to-quick-hunt-dialog/add-to-quick-hunt-dialog.component'
import { AddToTemplateDialogComponent } from '../../../shared/components/dialogs/add-to-template-dialog/add-to-template-dialog.component'
import { FeedbackModalComponent } from '../../../shared/components/dialogs/feedback-modal/feedback-modal.component'
import { HuntModuleDialogComponent } from '../../../shared/components/dialogs/hunt-module-dialog/hunt-module-dialog.component'
import { NewHuntDialogComponent } from '../../../shared/components/dialogs/new-hunt-dialog/new-hunt-dialog.component'
import { NewTemplateDialogComponent } from '../../../shared/components/dialogs/new-template-dialog/new-template-dialog.component'
import { PremiumDialogComponent } from '../../../shared/components/dialogs/premium-dialog/premium-dialog.component'
import { FacetButtonItem } from '../../../shared/components/facet-buttons/facet-buttons.component'
import { UserService } from '../../../shared/services'
import { CalendlyService } from '../../../shared/services/calendly.service'
import { CompanyService } from '../../../shared/services/company.service'
import { ContentService } from '../../../shared/services/content.service'
import { EnvironmentService } from '../../../shared/services/environment.service'
import { FavoriteService } from '../../../shared/services/favorite.service'
import { HuntPackageService } from '../../../shared/services/hunt-package.service'
import { ThreatProfileService } from '../../../shared/services/threat-profile.service'
import { BreadcrumbsDataService } from '../../services/breadcrumbs.service'
import { CollectionService } from '../../services/collection.service'
import { SearchUrlService } from '../../services/search-url.service'

type DeployStatus = 'notDeployed' | 'staged' | 'deployed' | 'outdated'

const NEW_OPTIONS = [
  'Existing Hunt Template',
  'Existing Hunt',
  'New Hunt Template',
  'New Hunt',
] as const

type MenuOption = (typeof NEW_OPTIONS)[number]

@Component({
  standalone: false,
  templateUrl: './hunt-package.component.html',
  styleUrls: ['./hunt-package.component.scss'],
})
export class DrillDownHuntPackageComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute)
  private bread = inject(BreadcrumbsDataService)
  private companyService = inject(CompanyService)
  private contentService = inject(ContentService)
  private huntPackageService = inject(HuntPackageService)
  private threatProfileService = inject(ThreatProfileService)
  private sanitizer = inject(DomSanitizer)
  private dialogService = inject(DialogService)
  private trackingService = inject(TrackingService)
  favoriteService = inject(FavoriteService)
  featureFlagService = inject(FeatureFlagService)
  environmentService = inject(EnvironmentService)
  calendlyService = inject(CalendlyService)
  router = inject(Router)
  collectionService = inject(CollectionService)
  authService = inject(AuthService)
  userService = inject(UserService)
  private searchUrlService = inject(SearchUrlService)

  public readonly queryLogicTooltipText =
    'This table exists as a tool agnostic language to inform analysts of how a query should work for your specific system. If you use one of the supported tools, it is recommended you use the available queries provided in the package.\n' +
    'Each selection in the table below represents an indicator or element of identifying suspicious or malicious acitivty in scope of the package. Some elements may be negated to indicate exceptions or filter known good activity. Not all selections may be necessary, and provided boolean logic will apply to create an effective combination.\n' +
    'The final line of the table represents boolean logic and how each selection should be combined to produce a query.'

  public huntContent: Array<ContentRecord> = []
  public detectionContent: Array<ContentRecord> = []
  public isHuntContentClosed = false
  public isDetectionContentClosed = false
  public isEnvContentClosed = false
  public data: HuntPackageRecord
  public favorited: boolean
  public huntPackageUUID: string
  public mitreAttackTactic: string
  public tags: FacetButtonItem[]
  public mitres = []
  public compatibleContent: Array<ContentRecord>
  public deployStatus: DeployStatus
  public validationImage = '/assets/brand/intel-bug-light.svg'
  public threatProfileUUID: string
  public contentDetails: ContentRecord
  public huntCompatible = false
  public expanded = false
  public priority = Enums.PriorityEnum
  public status = Enums.StatusEnum
  public outcome = Enums.OutcomeEnum
  public showAll = false
  public environments: Environment[]
  public tools: Array<string>
  public categories: Array<string>
  public contentForm: FormGroup
  public supportedToolUrl = supportedToolUrl
  public readonly snack = inject(SnackbarService)
  private themeService = inject(ThemeService)
  public theme = this.themeService.theme

  public newOptions = NEW_OPTIONS

  public options = [
    {
      actionName: 'Existing Hunt Template',
      click: (): void => {
        this.addToExistingTemplate()
      },
    },
    {
      actionName: 'Existing Hunt',
      click: (): void => {
        this.addToExistingQuickHunt()
      },
    },
    {
      actionName: 'New Hunt Template',
      click: (): void => {
        this.addToNewTemplate()
      },
    },
    {
      actionName: 'New Hunt',
      click: (): void => {
        this.addToNewQuickHunt()
      },
    },
  ]

  public generatedContentDetails: GeneratedContentDetails
  public generatedAddtionalContentDetails: Array<GeneratedContentDetails> = []
  public queryLogicData: Array<{
    field: string
    selection: string
    value: string
  }>
  public queryLogicCondition: string
  public displayedColumns: string[] = ['selection', 'field', 'value']
  public threatProfiles$: Observable<Array<ThreatProfile>>
  public huntModuleAccess = false
  public relatedHunts$: Observable<Array<HuntSummary>>
  public relatedCollections$: Observable<Array<CollectionRecord>>
  public locked$: Observable<boolean>
  private companyDetails: Company
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public environmentContent: any

  public ngOnInit(): void {
    // TODO: Drop need to call .getDetails(), since initial load call is handled by
    // resolver, and hunt package data is available on the route snapshot immediately
    this.data = this.route.snapshot.data.huntPackage
    this.tools = this.data.content.tools
    this.categories = this.data.content.categories
    this.route.params.subscribe((params) => {
      this.huntPackageUUID = params.hunt_package_uuid

      this.contentService
        .getHuntPackageContent(this.huntPackageUUID)
        .subscribe((content) => {
          this.environmentContent = content
        })
      this.getDetails()
      this.isFavorited()
      this.isHuntCompatible()
      this.getRelatedHunts()
      this.getRelatedCollections()
    })

    this.locked$ = this.userService.profile$.pipe(
      map((profile) => profile?.company.status === Enums.CompanyStatus.TRIAL),
    )

    this.environmentService.getAllEnvironments().subscribe((environments) => {
      this.environments = environments
    })
    this.huntModuleAccess = this.featureFlagService.huntModuleAccess()
    this.companyService.getCompanyDetails().subscribe((company) => {
      this.companyDetails = company
    })

    this.contentForm = new FormGroup({
      environments: new FormControl(),
      tools: new FormControl(),
      categories: new FormControl(),
    })

    this.trackingService.trackEvent(
      TrackingEventEnum.RESEARCH_HUNT_PACKAGE_ACCESSED,
      {
        UUID: this.data.UUID,
        title: this.data.title,
        context: this.data.context,
        mapping: this.data.mapping,
      },
    )
  }

  public goToEnvironmentSettings(): void {
    this.router.navigate(['/settings/environment'])
  }

  public openHuntDialog(): void {
    this.trackingService.trackEvent(
      TrackingEventEnum.COMMUNITY_HUNT_MODULE_DIALOG_OPENED,
    )
    const dialogRef: DialogRef = this.dialogService.open(
      HuntModuleDialogComponent,
      {
        panelClass: 'full-panel',
      },
    )

    dialogRef.afterClosed().subscribe((talkToCyborg: boolean) => {
      if (talkToCyborg) {
        this.talkToCyborg()
      } else {
        this.trackingService.trackEvent(
          TrackingEventEnum.COMMUNITY_HUNT_MODULE_DIALOG_ABANDONED,
        )
      }
    })
  }

  public openPremiumDialog(): void {
    this.trackingService.trackEvent(
      TrackingEventEnum.COMMUNITY_PREMIUM_DIALOG_OPENED,
    )
    const dialogRef: DialogRef = this.dialogService.open(
      PremiumDialogComponent,
      {
        panelClass: 'full-panel',
      },
    )

    dialogRef.afterClosed().subscribe((talkToCyborg: boolean) => {
      if (talkToCyborg) {
        this.talkToCyborg()
      } else {
        this.trackingService.trackEvent(
          TrackingEventEnum.COMMUNITY_PREMIUM_DIALOG_ABANDONED,
        )
      }
    })
  }

  public talkToCyborg(): void {
    this.calendlyService.talkToCyborg()
  }

  public isHuntCompatible(): void {
    this.huntCompatible = isHuntPackageHuntCompatible(this.data)
  }

  public addToExistingQuickHunt(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      AddToQuickHuntDialogComponent,
      { data: { huntPackages: [this.data] } },
    )

    dialogRef.afterClosed().subscribe((huntMeta: HuntPackagesAdded) => {
      if (huntMeta) {
        this.snack.show(
          `${huntMeta.huntPackagesAdded} new Hunt Package${huntMeta.huntPackagesAdded !== 1 ? 's' : ''} added to Hunt:`,
          5000,
          {
            text: huntMeta.name,
            callback: () => this.router.navigate(['hunt', huntMeta.id]),
          },
        )
      }
    })
  }

  public handleClose(): void {
    //test
  }

  public addToNewQuickHunt(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      NewHuntDialogComponent,
      { data: { huntPackages: [this.data] } },
    )
    dialogRef.afterClosed().subscribe((hunt: Hunt) => {
      if (hunt) {
        this.router.navigate(['hunt', hunt.id, 'workbench'])
      }
    })
  }

  public addToExistingTemplate(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      AddToTemplateDialogComponent,
      { data: { huntPackages: [this.data] } },
    )

    dialogRef.afterClosed().subscribe((templateMeta: HuntPackagesAdded) => {
      if (templateMeta)
        this.snack.show(
          `${templateMeta.huntPackagesAdded} new Hunt Package${templateMeta.huntPackagesAdded !== 1 ? 's' : ''} added to Hunt Template:`,
          5000,
          {
            text: templateMeta.name,
            callback: () =>
              this.router.navigate(['hunt', 'hunt-template', templateMeta.id]),
          },
        )
    })
  }

  public addToNewTemplate(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      NewTemplateDialogComponent,
      {
        data: {
          huntPackages: [this.data],
        },
      },
    )
    dialogRef.afterClosed().subscribe((result: HuntTemplate) => {
      if (result) this.router.navigate(['hunt', 'hunt-template', result.id])
    })
  }

  public populateHumanReadableTable(): void {
    const queryLogic: QueryLogic = this.data.query_logic
    if (queryLogic.table) {
      this.queryLogicCondition = this.data.query_logic.condition
      this.queryLogicData = queryLogic.table
        .map((item) => {
          const queryLogicDataArray: Array<{
            selection: string
            field: string
            value: string
          }> = []
          for (const [index, val] of item.value.entries()) {
            if (index === 0) {
              if (!item.grouped) {
                if (item.value.length > 1) {
                  queryLogicDataArray.push({
                    selection: item.selection + ' (ANY)',
                    field: item.field,
                    value: val,
                  })
                } else {
                  queryLogicDataArray.push({
                    selection: item.selection,
                    field: item.field,
                    value: val,
                  })
                }
              } else {
                if (item.value.length > 1) {
                  queryLogicDataArray.push({
                    selection: item.selection + ' (ALL)',
                    field: item.field,
                    value: val,
                  })
                } else {
                  queryLogicDataArray.push({
                    selection: item.selection,
                    field: item.field,
                    value: val,
                  })
                }
              }
            } else {
              queryLogicDataArray.push({
                selection: '',
                field: item.field,
                value: val,
              })
            }
          }
          return queryLogicDataArray
        })
        .flat()
    }
  }

  public ngOnDestroy(): void {
    this.bread.resetCrumb()
  }

  public getDeployStatus(): void {
    this.companyService.getDeployStatus(this.huntPackageUUID).subscribe(
      (deploy: Deploy & { status: DeployStatus }) => {
        this.deployStatus = deploy.status
      },
      () => {
        this.deployStatus = 'notDeployed'
      },
    )
  }

  public getRelatedHunts(): void {
    this.relatedHunts$ = this.huntPackageService.getRelatedHunts(
      this.huntPackageUUID,
    )
  }

  public getRelatedCollections(): void {
    this.relatedCollections$ =
      this.collectionService.getRelatedCollectionsByHuntPackageUUID(
        this.huntPackageUUID,
      )
  }

  public markAsStaged(huntPackageUUID): void {
    this.snack.show('Hunt Package Staged for Deploy', 5000)
    this.companyService.markAsStaged(huntPackageUUID).subscribe(() => {
      this.getDetails()
    })
  }

  public markAsDeployed(huntPackageUUID: string): void {
    this.snack.show('Hunt Package Marked Deployed', 5000)
    this.companyService.markAsDeployed(huntPackageUUID).subscribe(() => {
      this.getDetails()
    })
  }

  public removeDeployed(huntPackageUUID: string): void {
    this.snack.show('Hunt Package Removed', 5000)
    this.companyService.removeDeployed(huntPackageUUID).subscribe(() => {
      this.getDetails()
    })
  }

  private getThreatProfile(huntPackage: HuntPackageRecord): void {
    this.threatProfiles$ =
      this.threatProfileService.getThreatProfilebyThreatName(huntPackage)
  }

  private getDetails(): void {
    this.huntPackageService
      .getHuntPackageByUUID(this.huntPackageUUID)
      .subscribe((results: HuntPackageRecord) => {
        this.data = results

        this.huntContent = this.data.content.records
          .sort((a, b) => (a.tool > b.tool ? 1 : -1))
          .filter((content) => {
            return (
              content.goal == Enums.Goals.HUNT ||
              content.goal == Enums.Goals.VISUAL
            )
          })

        this.detectionContent = this.data.content.records
          .sort((a, b) => (a.tool > b.tool ? 1 : -1))
          .filter((content) => {
            return (
              content.goal == Enums.Goals.BEHAVIOR ||
              content.goal == Enums.Goals.TARGETED
            )
          })

        this.getDeployStatus()
        if (this.data.context.threat_names.length > 0) {
          this.getThreatProfile(this.data)
        }
        this.populateHumanReadableTable()
        this.isHuntCompatible()
        this.tags = gatherTags(this.data)

        const subInfo = {
          type: 'Hunt Package',
          url: `hunt-package/${this.data.UUID}`,
          title: this.data.title,
        }

        if (
          this.data.validation_types.includes('red_canary') ||
          this.data.validation_types.includes('cyborg_redcanary')
        ) {
          this.validationImage = '/assets/img/validation/red_canary_small.png'
        }

        this.bread.setSubCrumb(subInfo)
        this.mitreAttackTactic = this.data?.mapping?.mitre?.mitre_attack_tactics
          .toString()
          .replace(',', ', ')

        this.mitres = []
        for (
          let i = 0;
          i < this.data.mapping.mitre.mitre_attack_techniques.length;
          i += 1
        ) {
          const mitre = { technique: '', id: '' }
          mitre.technique = this.data.mapping.mitre.mitre_attack_techniques[i]
          mitre.id = this.data.mapping.mitre.mitre_attack_technique_ids[i]
          this.mitres.push(mitre)
        }
      })
  }

  public downloadValidation(filename: string): void {
    this.trackingService.trackEvent(
      TrackingEventEnum.RESEARCH_EMULATION_VALIDATION_DOWNLOADED,
      {
        UUID: this.data.UUID,
        title: this.data.title,
      },
    )

    this.huntPackageService.downloadHuntPackageValidation(
      this.huntPackageUUID,
      filename,
    )
  }
  private isFavorited(): void {
    this.favoriteService
      .checkHuntPackageFavorite(this.huntPackageUUID)
      .subscribe((favorite: boolean) => {
        this.favorited = favorite
      })
  }

  public toggleShowAll(): void {
    this.showAll = !this.showAll
  }

  public onHuntContentClose(): void {
    this.isHuntContentClosed = !this.isHuntContentClosed
  }

  public onEnvContentClose(): void {
    this.isEnvContentClosed = !this.isEnvContentClosed
  }

  public onDetectionContentClose(): void {
    this.isDetectionContentClosed = !this.isDetectionContentClosed
  }

  public onCopy(target: EventTarget): void {
    const text = (target as HTMLElement).parentNode as HTMLElement

    if (this.supportedToolUrl(this.generatedContentDetails.tool))
      navigator.clipboard.writeText(text.children[3].textContent.trim())
    else navigator.clipboard.writeText(text.children[2].textContent.trim())

    this.snack.show('Content Copied', 5000)
  }

  public scrollTo(target: string): void {
    document.getElementById(target).scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    })
  }

  public favorite(): void {
    this.favoriteService
      .favoriteHuntPackage(this.huntPackageUUID)
      .subscribe(() => {
        this.favorited = true
      })
  }
  public unfavorite(): void {
    this.favoriteService
      .unfavoriteHuntPackage(this.huntPackageUUID)
      .subscribe(() => {
        this.favorited = false
      })
  }

  public onFeedback(): void {
    this.dialogService.open(FeedbackModalComponent, {
      data: {
        uuid: this.huntPackageUUID,
      },
    })
  }

  public getContent(content: ContentRecord): void {
    this.contentService.getContent(content).subscribe((generatedContent) => {
      this.generatedContentDetails = generatedContent
    })
  }

  //Seperate function due to multiple additional queries
  public getAdditionalContent(content: ContentRecord): void {
    this.contentService
      .getAdditionalContent(content)
      .subscribe((allGeneratedContent) => {
        this.generatedAddtionalContentDetails = allGeneratedContent
      })
  }

  public filterContent(): void {
    this.contentService
      .getHuntPackageContent(this.huntPackageUUID, this.contentForm.value)
      .subscribe((content) => {
        this.environmentContent = content
      })
  }

  /**
   * Builds the Tool URI based on the contentTool selected and the content of the record
   */
  public getSearchURLForContent(
    contentTool: SupportedTools,
    content: string,
  ): string {
    return toolFactory(contentTool).generateSearchPermalink(
      this.getUserToolRootURL(contentTool),
      content,
    )
  }

  public clearAllFilters(): void {
    this.contentForm.reset()
    this.filterContent()
  }

  public handleMenuAction(event: { value: MenuOption }): void {
    switch (event.value) {
      case 'Existing Hunt Template':
        this.addToExistingTemplate()
        break
      case 'Existing Hunt':
        this.addToExistingQuickHunt()
        break
      case 'New Hunt Template':
        this.addToNewTemplate()
        break
      case 'New Hunt':
        this.addToNewQuickHunt()
        break
    }
  }

  /**
   * Returns user's tool root URL for the given tool, or returns null if not set
   */
  public getUserToolRootURL(contentTool: SupportedTools): string | null {
    return this.companyDetails?.toolMetadata?.[contentTool]?.url
  }
}
